import React, { useEffect, useState, memo, useContext } from "react";
import moment from "moment";
import { Button, Col, Image, Input, notification, Row, Select, Skeleton, Spin, Typography, Tooltip } from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CloseOutlined, CheckOutlined } from "@ant-design/icons";
import { IConsumableDetails } from "@Models/IConsumableDetails";
import { IConsumablePayload } from "@Models/IConsumablePayload";
import { IConsumableDrawDownDetails } from "@Models/IConsumableDrawDownDetails";
import CreditService from "@Services/CreditService";
import ConsumablesService from "@Services/ConsumablesService";
import SessionManager from "@Core/session";
import ConsumableItem from "@Components/consumables/ConsumableItem";
import "@Styles/pages/supplySuitePage.scss";
import PageHelmet from "@Components/shared/PageHelmet";
import { GroupContext, QueryKeys, ThreeMinutes } from "@Constants";
import { ICreditDetails } from "@Models/ICreditDetails";
import { MainContext } from "@Context/provider";
import { IAddressDetails } from "@Models/IAddressDetails";
import AddressService from "@Services/AddressService";
import CountryFlag from "@Components/user/CountryFlag";
import { Countries } from "../../countries";
import { isNil, isPresent } from "@Utils";
const { Title } = Typography;
const { Option } = Select;

const SupplySuitePage: React.FC = () => {
    const { ui: uiState } = useContext(MainContext);
    const [consumable, setConsumable] = useState<IConsumablePayload>({ products: [], consumables: [] });
    const [renewalDay, setRenewalDay] = useState<string>();
    const [fetching, setFetching] = useState<boolean>(false);
    const [fetchingAddresses, setFetchingAddresses] = useState<boolean>(false);
    const [filteredConsumables, setFilteredConsumables] = useState<IConsumableDetails[]>([]);
    const [checkoutList, setCheckoutList] = useState<[number, IConsumableDetails][]>([]);
    const [creditsUsed, setCreditsUsed] = useState<{ totalUsedCredits: number; remainingCredits: number }>();
    const [addresses, setAddresses] = useState<IAddressDetails[]>([]);
    const [addressDefaultOption, setAddressDefaultOption] = useState<IAddressDetails>(null);
    const [currentAddress, setCurrentAddress] = useState<IAddressDetails>(null);
    const [filters, setFilters] = useState<{ products: string[]; search: string }>({ products: [], search: "" });
    const queryClient = useQueryClient();

    const getAllConsumables = async () => {
        const service = new ConsumablesService();
        const res = await service.getAll();
        if (!res.hasErrors) {
            setConsumable(res.value);
            setFilteredConsumables(res.value.consumables);
        } else {
            notification.error({ message: "Error retrieving drawdown items." });
        }
    };

    /**
     * Get the label for this address for use in the selector.
     * @param address
     */
    const getLabel = (address: IAddressDetails) => {
        if (address == null) return null;
        let label = "";
        if (address.line1 !== null && address.line1 !== "") {
            label = address.line1;
        } else if (address.line2 !== null && address.line2 !== "") {
            label = address.line2;
        } else if (address.line3 !== null && address.line3 !== "") {
            label = address.line3;
        }

        if (address.postCode !== null && address.postCode !== "") {
            if (label !== "") {
                label += ", ";
            }
            label += address.postCode;
        }
        if (label === "") {
            const index = addresses?.findIndex((x) => x.addressId === address.addressId) + 1;
            label = "Address " + index;
        }

        return label;
    };

    const isAddressOptionDisabled = (address: IAddressDetails) => {
        const fields = ["phone", "line1", "postCode", "countryCode"];
        return fields.some((field) => isNil(address[field]) || address[field] === "");
    };

    const addressOptions = addresses?.map((x) => {
        return { label: getLabel(x), value: x?.addressId, disabled: isAddressOptionDisabled(x) };
    });

    useEffect(() => {
        if (addresses) {
            const defaultAddress = addresses.find((address) => !isAddressOptionDisabled(address));
            setAddressDefaultOption(defaultAddress);
        } else {
            setAddressDefaultOption(null);
        }
    }, [addresses]);

    const getAddresses = async () => {
        if (getCurrent() == null) return;
        setFetchingAddresses(true);
        const service = new AddressService();
        const addressesResult =
            getCurrent()?.contextId === GroupContext
                ? await service.getGroup([getCurrent()?.instanceId])
                : await service.getUser([getCurrent()?.instanceId]);
        setFetchingAddresses(false);
        if (!addressesResult.hasErrors) {
            setAddresses(addressesResult.value);
            if (addressesResult.value.length > 0) {
                const addressDefaultOption = addressesResult.value.find((address) => !isAddressOptionDisabled(address));
                setCurrentAddress(addressDefaultOption);
            }
        } else {
            setAddresses(null);
        }
    };

    const creditQuery = useQuery(
        [QueryKeys.UserCredits],
        async () => {
            const service = new CreditService();
            const credits = await service.getUserFull(SessionManager.getServiceUser()?.userId);
            if (!credits.hasErrors) {
                return credits.value;
            }
            return [];
        },
        {
            enabled: !!SessionManager.getServiceUser(),
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
        }
    );

    /**
     * Get the current credit details based on the UI state's selected credit.
     */
    const getCurrent = (): ICreditDetails => {
        if (isPresent(creditQuery.data)) {
            const index = creditQuery.data.findIndex((x) => x.creditId === uiState.currentCredit?.instance);
            if (index !== -1) return creditQuery?.data[index];
        }
        return null;
    };

    const onCategoriesSelectChange = (value: string[]) => {
        setFilters((prevState) => ({
            ...prevState,
            products: value,
        }));
    };

    const onCategoriesSelectClear = () => {
        setFilters((prevState) => ({
            ...prevState,
            products: [],
        }));
    };

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = e;
        setFilters((prevState) => ({
            ...prevState,
            search: value || "",
        }));
    };

    /**
     * Switch address.
     * @param value
     */
    const onAddressSelect = async (value) => {
        const current = addresses?.find((x) => x.addressId === value);
        setCurrentAddress(current);
    };

    const onAddConsumable = (item: IConsumableDetails, amount: number) => {
        setCheckoutList((prevState) => [...prevState, [amount, item]]);
    };

    const onRemoveConsumable = (id: string, index: number) => {
        const item: number = checkoutList.findIndex((el, i) => el[1].consumableId === id && index === i);
        const list = checkoutList;
        list.splice(item, 1);
        setCheckoutList(() => [...list]);
    };

    const onConfirmDrawdown = async () => {
        if (checkoutList.length) {
            setFetching(true);
            const service = new ConsumablesService();
            const model: IConsumableDrawDownDetails = {
                ownerId: SessionManager.getServiceUser()?.userId,
                address: currentAddress
                    ? {
                          line1: currentAddress.line1,
                          line2: currentAddress.line2,
                          line3: currentAddress.line3,
                          city: currentAddress.city,
                          county: currentAddress.county,
                          postCode: currentAddress.postCode,
                          countryCode: currentAddress.countryCode,
                          email: currentAddress.email,
                          phone: currentAddress.phone,
                      }
                    : null,
                items: checkoutList.map((item) => ({ consumableId: item[1].consumableId, quantity: item[0] })),
                status: "",
                comment: "",
            };
            await service.createOrder(getCurrent()?.creditId, model).then((res) => {
                if (!res.errors.length) {
                    queryClient.invalidateQueries([QueryKeys.UserCredits]);
                    notification.success({ message: "Thanks, drawdown request accepted." });
                    setCheckoutList([]);
                } else {
                    notification.error({ message: "Sorry, error processing this request." });
                }
            });
            setFetching(false);
        }
    };

    const renderAddress = (): JSX.Element[] | string | JSX.Element => {
        if (fetchingAddresses) {
            return <Spin style={{ height: "100px", width: "100px", backgroundColor: "transparent" }} />;
        } else if (!isNil(currentAddress)) {
            const result: JSX.Element[] = [];
            if (currentAddress == null) return result;
            if (currentAddress.line1 !== "") result.push(<div>{currentAddress.line1}</div>);
            if (currentAddress.line2 !== "") result.push(<div>{currentAddress.line2}</div>);
            if (currentAddress.line3 !== "") result.push(<div>{currentAddress.line3}</div>);
            if (currentAddress.city !== "") result.push(<div>{currentAddress.city}</div>);
            if (currentAddress.county !== "") result.push(<div>{currentAddress.county}</div>);
            if (currentAddress.postCode !== "") result.push(<div>{currentAddress.postCode}</div>);
            if (currentAddress.countryCode !== "")
                result.push(
                    <div>
                        <span>
                            <CountryFlag countryCode={currentAddress.countryCode ?? "GB"} />
                        </span>
                        <span style={{ paddingLeft: "10px" }}>{Countries.get(currentAddress.countryCode ?? "GB")}</span>
                    </div>
                );
            if (currentAddress.email !== "" || currentAddress.phone !== "")
                result.push(
                    <div>
                        <br />
                    </div>
                );
            if (currentAddress.email !== "") result.push(<div>{currentAddress.email}</div>);
            if (currentAddress.phone !== "") result.push(<div>{currentAddress.phone}</div>);
            return result;
        } else {
            return "Please add an address to confirm drawdown";
        }
    };

    /**
     * Hook for applying all filters
     */
    useEffect(() => {
        let result: IConsumableDetails[];
        result = consumable.consumables.filter((item) => {
            return (
                item.name.toLowerCase().indexOf(filters.search.toLowerCase()) !== -1 ||
                item.productCode.toLowerCase().indexOf(filters.search.toLowerCase()) !== -1
            );
        });

        if (filters.products.length) {
            result = result.filter((item) => {
                let res = false;
                for (const val of item.productIds) {
                    res = !!filters.products.find((el) => el === val.id);
                }
                return res;
            });
        }
        setFilteredConsumables(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        // Recalculate used credits
        const totalUsedCredits = checkoutList.reduce(
            (previousValue, currentValue) => previousValue + currentValue[0] * currentValue[1].creditPrice,
            0
        );

        const remainingCredits: number = getCurrent()?.creditValue - totalUsedCredits;
        setCreditsUsed({ totalUsedCredits, remainingCredits });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutList]);

    // Check next renewal date
    useEffect(() => {
        if (getCurrent() === null) return;
        const date = new Date();
        let renewalDay = getCurrent()?.renewalDay;
        let result = moment(new Date(date.getFullYear(), date.getMonth(), renewalDay)).format("MMM Do YYYY");
        const numberDaysNextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const numberDaysThisMonth = new Date(date.getFullYear(), date.getMonth(), 0).getDate();

        if (date.getDay() > renewalDay && renewalDay > numberDaysNextMonth) {
            renewalDay = Math.min(getCurrent()?.renewalDay, numberDaysNextMonth);
            result = moment(new Date(date.getFullYear(), date.getMonth() + 1, renewalDay)).format("MMM Do YYYY");
        }
        if (date.getDay() < renewalDay && renewalDay > numberDaysThisMonth) {
            renewalDay = Math.min(getCurrent()?.renewalDay, numberDaysThisMonth);
            result = moment(new Date(date.getFullYear(), date.getMonth(), renewalDay)).format("MMM Do YYYY");
        }
        if (date.getDay() > renewalDay && !(renewalDay > numberDaysNextMonth)) {
            result = moment(new Date(date.getFullYear(), date.getMonth() + 1, renewalDay)).format("MMM Do YYYY");
        }

        setRenewalDay(result);
    }, [getCurrent()]);

    useEffect(() => {
        const fetch = async () => {
            await getAllConsumables();
            await queryClient.invalidateQueries(["userCredits"]);
        };
        fetch().then(() => getAddresses());
    }, []);

    useEffect(() => {
        setCheckoutList([]);
        setCurrentAddress(null);
        getAddresses().then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiState.currentCredit]);

    const productIdsOptions = consumable?.products.map((item) => ({ value: item.productId, label: item.label }));
    const activeFilters = filters.search !== "" || filters.products.length;

    return (
        <article className="consumables-page">
            <PageHelmet name={"Supply Suite"} />
            <Row gutter={[16, 16]}>
                <Col xl={12} lg={24} md={24} sm={24} xs={24}>
                    <section className="products">
                        <div className="filter-panel">
                            <Select
                                placeholder="Select categories"
                                className="products-select"
                                onChange={onCategoriesSelectChange}
                                onClear={onCategoriesSelectClear}
                                mode="multiple"
                                allowClear
                                options={productIdsOptions}
                            />
                            <Input
                                placeholder="Search code or product name"
                                allowClear
                                onChange={onSearchChange}
                                className="products-search"
                            />
                        </div>
                        <div className="consumables-list">
                            {!filteredConsumables.length && !activeFilters ? (
                                <>
                                    <Skeleton avatar paragraph={{ rows: 4 }} />
                                    <Skeleton avatar paragraph={{ rows: 4 }} />
                                    <Skeleton avatar paragraph={{ rows: 4 }} />
                                </>
                            ) : (
                                filteredConsumables.map((item) => (
                                    <ConsumableItem
                                        {...item}
                                        key={item.consumableId}
                                        addConsumable={onAddConsumable}
                                        creditDetails={creditQuery?.data[0]}
                                        remainingCredits={creditsUsed.remainingCredits}
                                    />
                                ))
                            )}
                        </div>
                    </section>
                </Col>
                <Col xl={12} lg={24} md={24} sm={24} xs={24}>
                    <section className="consumables-page-checkout">
                        <Title level={4} className="credit-account">
                            Account: {getCurrent()?.description}
                        </Title>
                        <div className="credits-panel">
                            <Title level={4} className="credits">
                                Credits: {getCurrent()?.creditValue}
                            </Title>
                            <div className="supply-suite">
                                <p>
                                    Shipments remaining: <span>{getCurrent()?.drawDownValue}</span>
                                </p>
                                <p>
                                    Next drawdown will be added to your account: <span>{renewalDay}</span>
                                </p>
                            </div>
                        </div>
                        <div className="address-panel" style={{ marginTop: "10px" }}>
                            <div className="address-header">
                                <Title level={4} className="address">
                                    Address:
                                </Title>
                                {addresses?.length > 1 && !fetchingAddresses && (
                                    <Select
                                        defaultValue={{
                                            label: getLabel(addressDefaultOption),
                                            value: addressDefaultOption?.addressId,
                                        }}
                                        value={{ label: getLabel(currentAddress), value: currentAddress?.addressId }}
                                        style={{ flexGrow: 1 }}
                                        onChange={(newValue) => onAddressSelect(newValue)}
                                    >
                                        {addressOptions.map((address) => {
                                            return (
                                                <Option value={address.value} disabled={address.disabled}>
                                                    {address.disabled ? (
                                                        <Tooltip title="Line 1, phone number, country and post/zip code are required. Please amend your address.">
                                                            <div style={{ width: "100%" }}>{address.label}</div>
                                                        </Tooltip>
                                                    ) : (
                                                        <div> {address.label}</div>
                                                    )}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                )}
                            </div>
                            <div style={{ paddingBottom: "20px" }}>{renderAddress()}</div>
                        </div>
                        <div className="confirm-drawdown-panel">
                            <div className="consumables-items-list">
                                <Title level={4} className="credits-used">
                                    Credits used: <span>{creditsUsed?.totalUsedCredits}</span>
                                </Title>
                                <ul>
                                    {checkoutList.map((item: [number, IConsumableDetails], index) => (
                                        <li key={`${item[1].productCode}${index}`}>
                                            <div>
                                                <h4>Name: {item[1].name}</h4>
                                                <p>Product Code: {item[1].productCode}</p>
                                                <p>Credits: {item[1].creditPrice}</p>
                                                <p>Quantity: {item[0]}</p>
                                            </div>
                                            <div className="flex flex-justify-space-between">
                                                <Image className="consumable-image" src={item[1].imageUrl} />
                                                <CloseOutlined
                                                    style={{ fontSize: 18 }}
                                                    className="remove-icon"
                                                    onClick={() => onRemoveConsumable(item[1].consumableId, index)}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <Button
                                size="large"
                                icon={<CheckOutlined />}
                                disabled={!checkoutList.length || fetching || isNil(currentAddress)}
                                onClick={onConfirmDrawdown}
                                loading={fetching}
                                className="secondary"
                            >
                                Confirm Drawdown
                            </Button>
                        </div>
                    </section>
                </Col>
            </Row>
        </article>
    );
};
export default memo(SupplySuitePage);
